import { chatApi } from '@/store/api/conversation/chat/chat.api';
import { dropChatMessage, writeChatMessage } from '@/store/api/conversation/chat/chat.cache';
import { dropChatPinned, syncChatPinned } from '@/store/api/conversation/chat/chat.manage.cache';
import type { TypeChatMessage } from '@/types/conversation/types.conversation.chat.message';

type TypeChatBulkOutcome = { channelId: string; done: string[]; refused: Array<{ id: string; message: string }> };

const PINNED_TAG = 'ChatPinned' as const;

export const newestPinFirst = (a: TypeChatMessage, b: TypeChatMessage): number => ((b.pinnedAt ?? '') > (a.pinnedAt ?? '') ? 1 : (b.pinnedAt ?? '') < (a.pinnedAt ?? '') ? -1 : 0);

const messageUrl = (id: string, rest: string) => `/conversations/chat/messages/${encodeURIComponent(id)}${rest}`;

export const chatManageApi = chatApi.enhanceEndpoints({ addTagTypes: [PINNED_TAG] }).injectEndpoints({
    endpoints: (build) => ({
        pinChatMessage: build.mutation<TypeChatMessage, { id: string; pinned: boolean }>({
            query: ({ id, pinned }) => ({ url: messageUrl(id, '/pin'), method: 'post', data: { pinned } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeChatMessage(dispatch, data);
                    syncChatPinned(dispatch, data);
                } catch {
                    return;
                }
            },
        }),

        listChatPinned: build.query<TypeChatMessage[], string>({
            query: (channelId) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/pinned` }),
            transformResponse: (response: TypeChatMessage[] | null) => (response ?? []).slice().sort(newestPinFirst),
            providesTags: (_result, _error, channelId) => [{ type: PINNED_TAG, id: channelId }],
            keepUnusedDataFor: 300,
        }),

        forwardChatMessage: build.mutation<TypeChatMessage[], { id: string; channelIds: string[]; ephemeral: number }>({
            query: ({ id, channelIds, ephemeral }) => ({ url: messageUrl(id, '/forward'), method: 'post', data: { channelIds, ephemeral } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const message of data) writeChatMessage(dispatch, message);
                } catch {
                    return;
                }
            },
        }),

        removeChatMessages: build.mutation<TypeChatBulkOutcome, { channelId: string; messageIds: string[] }>({
            query: ({ channelId, messageIds }) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/messages/remove`, method: 'post', data: { messageIds } }),
            onQueryStarted: async ({ channelId }, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const id of data.done) {
                        dropChatMessage(dispatch, { id, channelId, reason: 'deleted' });
                        dropChatPinned(dispatch, { id, channelId });
                    }
                } catch {
                    return;
                }
            },
        }),

        forwardChatMessages: build.mutation<TypeChatBulkOutcome & { created: TypeChatMessage[] }, { channelId: string; messageIds: string[]; channelIds: string[]; ephemeral: number }>({
            query: ({ channelId, ...data }) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/messages/forward`, method: 'post', data }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const message of data.created) writeChatMessage(dispatch, message);
                } catch {
                    return;
                }
            },
        }),
    }),
});

export const { usePinChatMessageMutation, useListChatPinnedQuery, useForwardChatMessageMutation, useRemoveChatMessagesMutation, useForwardChatMessagesMutation } = chatManageApi;

export default chatManageApi;
